import { useState, useCallback } from 'react';
import { categories as categoriesApi } from '@/lib/api';
import type {
  Category,
  CreateCategoryData,
  UpdateCategoryData,
} from '@/lib/types';

interface UseCategoriesReturn {
  categories: Category[];
  loading: boolean;
  error: string | null;
  fetchCategories: () => Promise<void>;
  createCategory: (data: CreateCategoryData) => Promise<Category>;
  updateCategory: (id: string, data: UpdateCategoryData) => Promise<Category>;
  deleteCategory: (id: string) => Promise<void>;
}

export function useCategories(): UseCategoriesReturn {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchCategories = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await categoriesApi.list();
      setCategories(data);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to fetch categories';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  const createCategory = useCallback(
    async (data: CreateCategoryData): Promise<Category> => {
      setError(null);
      try {
        const category = await categoriesApi.create(data);
        await fetchCategories();
        return category;
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to create category';
        setError(message);
        throw err;
      }
    },
    [fetchCategories]
  );

  const updateCategory = useCallback(
    async (id: string, data: UpdateCategoryData): Promise<Category> => {
      setError(null);
      try {
        const category = await categoriesApi.update(id, data);
        await fetchCategories();
        return category;
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to update category';
        setError(message);
        throw err;
      }
    },
    [fetchCategories]
  );

  const deleteCategory = useCallback(
    async (id: string): Promise<void> => {
      setError(null);
      try {
        await categoriesApi.delete(id);
        await fetchCategories();
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to delete category';
        setError(message);
        throw err;
      }
    },
    [fetchCategories]
  );

  return {
    categories,
    loading,
    error,
    fetchCategories,
    createCategory,
    updateCategory,
    deleteCategory,
  };
}
